/**
 * Upload Service
 * Handles file uploads to backend for manual ingestion (Stage 6.6)
 */

import { supabase } from '../lib/supabase';
import API_URL from '../config/api';

/**
 * Check if a file with the same name already exists in user's items
 * @param {string} userId - User ID
 * @param {string} fileName - File name to check
 * @returns {Promise<{exists: boolean, existingItem?: object}>}
 */
export async function checkDuplicateFile(userId, fileName) {
  try {
    // Normalize filename for comparison
    const normalizedName = fileName.toLowerCase().trim();
    
    // Check in deepsearch.items by looking at the URL pattern
    const { data: items, error } = await supabase
      .schema('deepsearch')
      .from('items')
      .select('id, title, url, image_url, created_at')
      .eq('user_id', userId)
      .or(`url.ilike.%${normalizedName}%,title.ilike.%${normalizedName}%`);
    
    if (error) {
      console.warn('Error checking for duplicates:', error);
      return { exists: false };
    }
    
    if (items && items.length > 0) {
      // Check for exact filename match in URL
      const exactMatch = items.find(item => {
        const url = item.url || item.image_url || '';
        const urlFileName = url.split('/').pop()?.split('?')[0]?.toLowerCase() || '';
        return urlFileName.includes(normalizedName) || normalizedName.includes(urlFileName.replace(/\.[^.]+$/, ''));
      });
      
      if (exactMatch) {
        return { exists: true, existingItem: exactMatch };
      }
    }
    
    return { exists: false };
  } catch (error) {
    console.warn('Error in duplicate check:', error);
    return { exists: false };
  }
}

/**
 * Check multiple files for duplicates
 * @param {string} userId - User ID
 * @param {File[]} files - Array of files to check
 * @returns {Promise<{duplicates: Array<{file: File, existingItem: object}>, newFiles: File[]}>}
 */
export async function checkDuplicateFiles(userId, files) {
  const duplicates = [];
  const newFiles = [];
  
  for (const file of files) {
    const { exists, existingItem } = await checkDuplicateFile(userId, file.name);
    if (exists) {
      duplicates.push({ file, existingItem });
    } else {
      newFiles.push(file);
    }
  }
  
  return { duplicates, newFiles };
}

/**
 * Upload files to backend for processing
 * @param {File[]} files - Array of files to upload
 * @param {string} caption - Optional caption/description
 * @param {string} collectionId - Collection/topic ID to add files to
 * @returns {Promise<{status: string, count: number, items: Array}>}
 */
export async function uploadFiles(files, caption = '', collectionId = null) {
  try {
    // Get current session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      throw new Error('Not authenticated. Please log in.');
    }

    if (!collectionId) {
      throw new Error('Collection ID is required');
    }

    // Create FormData
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    
    if (caption) {
      formData.append('caption', caption);
    }
    
    // Add userId and collectionId to form data
    formData.append('userId', session.user.id);
    formData.append('collectionId', collectionId);

    // Upload to backend with timeout
    // Extract base URL from API_URL (which includes /v1) or default to localhost:4028
    let baseUrl;
    if (API_URL && API_URL.includes('/v1')) {
      baseUrl = API_URL.replace('/v1', '');
    } else {
      // Default to localhost:4028 (ingestion server port)
      baseUrl = 'http://localhost:4028';
    }
    const uploadUrl = `${baseUrl}/v1/ingestion/upload`;
    
    console.log('🔍 [Upload Service] Configuration:', {
      API_URL,
      baseUrl,
      uploadUrl,
      port: baseUrl.match(/:(\d+)/)?.[1] || 'unknown'
    });
    
    console.log('🚀 [UPLOAD SERVICE] Starting upload...');
    console.log('🚀 Uploading to:', uploadUrl);
    console.log('🔑 Using token:', session.access_token ? `${session.access_token.slice(0, 20)}...` : 'NO TOKEN');
    console.log('📦 FormData entries:', {
      filesCount: files.length,
      hasCaption: !!caption,
      hasUserId: !!session.user?.id,
      hasCollectionId: !!collectionId,
      collectionId: collectionId,
      collectionIdType: typeof collectionId,
    });
    
    // Log file details
    files.forEach((file, idx) => {
      console.log(`📄 File ${idx + 1}:`, {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: new Date(file.lastModified).toISOString(),
      });
    });
    
    // Verify collectionId is actually in FormData
    if (!collectionId) {
      console.error('❌ CRITICAL: collectionId is missing from FormData!');
      throw new Error('Collection ID is required for upload');
    }
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.error('⏰ Upload timeout after 30 seconds');
      controller.abort();
    }, 30000); // Reduced to 30 seconds
    
    let response;
    try {
      console.log('📤 [UPLOAD SERVICE] Sending fetch request...');
      response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          // Don't set Content-Type - let browser set it with boundary for multipart/form-data
        },
        body: formData,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      console.log('📥 [UPLOAD SERVICE] Response received:', response.status, response.statusText);
      console.log('📥 [UPLOAD SERVICE] Response headers:', Object.fromEntries(response.headers.entries()));
    } catch (fetchError) {
      clearTimeout(timeoutId);
      console.error('❌ Fetch error:', fetchError);
      if (fetchError.name === 'AbortError') {
        throw new Error('Upload timeout: The request took too long. Please check your connection and try again.');
      }
      throw new Error(`Network error: ${fetchError.message}`);
    }

    if (!response.ok) {
      console.error('❌ [UPLOAD SERVICE] Upload failed with status:', response.status);
      const errorText = await response.text();
      console.error('❌ [UPLOAD SERVICE] Error response body:', errorText);
      let errorMessage = `Upload failed: ${response.status} ${response.statusText}`;
      
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
        console.error('❌ [UPLOAD SERVICE] Parsed error:', errorData);
      } catch (e) {
        // If not JSON, use the text as-is
        errorMessage = errorText || errorMessage;
        console.error('❌ [UPLOAD SERVICE] Error response is not JSON');
      }

      // Handle specific error codes
      if (response.status === 413) {
        throw new Error('File too large. Maximum size is 50MB per file.');
      } else if (response.status === 429) {
        throw new Error('Rate limit exceeded. Please wait a moment and try again.');
      } else if (response.status === 400) {
        throw new Error(errorMessage);
      } else if (response.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }

      throw new Error(errorMessage);
    }

    const result = await response.json();
    
    // Unwrap ResponseInterceptor format: { success: true, data: {...} }
    if (result.success && result.data) {
      return result.data;
    }
    
    return result;
  } catch (error) {
    console.error('Upload service error:', error);
    throw error;
  }
}

/**
 * Validate file before upload
 * @param {File} file - File to validate
 * @returns {{valid: boolean, error?: string}}
 */
export function validateFile(file) {
  const MAX_SIZE = 50 * 1024 * 1024; // 50MB
  const ALLOWED_TYPES = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'video/mp4',
    'video/webm',
    'video/quicktime',
  ];

  if (file.size > MAX_SIZE) {
    return {
      valid: false,
      error: `File "${file.name}" is too large. Maximum size is ${MAX_SIZE / 1024 / 1024}MB.`,
    };
  }

  if (!ALLOWED_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: `File type "${file.type}" is not supported. Allowed types: images, PDFs, and videos.`,
    };
  }

  return { valid: true };
}

/**
 * Validate multiple files
 * @param {File[]} files - Array of files to validate
 * @returns {{valid: boolean, errors: string[]}}
 */
export function validateFiles(files) {
  const MAX_FILES = 10;
  const errors = [];

  if (files.length === 0) {
    return { valid: false, errors: ['Please select at least one file.'] };
  }

  if (files.length > MAX_FILES) {
    return { valid: false, errors: [`Maximum ${MAX_FILES} files allowed per upload.`] };
  }

  files.forEach(file => {
    const validation = validateFile(file);
    if (!validation.valid) {
      errors.push(validation.error);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
  };
}

